import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import { Avatar, Box, Button, Card, Flex, Heading, HStack, Link, Menu, Portal, Text, VStack, Wrap } from '@chakra-ui/react';
import { AiAvatarBadge } from '@src/components/sco/Account/UserAvatar';
import { ASK_PROMPTS } from '@src/components/sco/Advisors/data/askPrompts';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { useWilMessageUsage } from '@src/components/sco/Advisors/hooks/useWilMessageUsage';
import { useWilSessions } from '@src/components/sco/Advisors/hooks/useWilSessions';
import { useWilTool } from '@src/components/sco/Advisors/hooks/useWilTool';
import { useSetAdvisorsBelowToolbar, useSetAdvisorsToolbarRightElements } from '@src/components/sco/Advisors/layout/AdvisorsToolbarExtras';
import { WilWhoIsModal } from '@src/components/sco/Advisors/modals/WilWhoIsModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { WilMessagePaywall } from '@src/components/sco/Advisors/shared/WilMessagePaywall';
import { clipTitle, isMeaningfulTitle, sessionSubtitle, sessionTitle } from '@src/components/sco/Advisors/utils/wilSessionTitle';
import { ChatMessageComposer } from '@src/components/sco/Chat/ChatMessageComposer';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import type { ScoToolSession } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuCheck, LuChevronDown, LuMessageSquarePlus } from 'react-icons/lu';
const WIL_SESSION_QUERY = gql`
  query AdvisorWilSession($toolSessionId: String!) {
    scoToolSession(toolSessionId: $toolSessionId) {
      id
      toolSessionId
      title
      createdAt
      updatedAt
      generatedSummary
      threadId
      thread {
        id
        threadId
        commentsCount
        lastActiveAt
        comments(pageSize: 5, order: [["createdAt", "asc"]]) {
          id
          items {
            id
            body
            createdByUserId
            createdByUser {
              id
              userId
            }
          }
        }
      }
    }
  }
`;
const WIL_THREAD_COMMENTS_COUNT = gql`
  query WilThreadCommentsCount($threadId: String!) {
    forum {
      id
      thread(threadId: $threadId) {
        id
        commentsCount
      }
    }
  }
`;
const UPSERT_TOOL_SESSION = gql`
  mutation CreateAdvisorWilSession($input: UpsertToolSessionInput!) {
    upsertToolSession(input: $input) {
      toolSession {
        id
        toolSessionId
        title
        threadId
        thread {
          id
          threadId
        }
      }
    }
  }
`;
const WIL_THREAD_COMMENT_SUBSCRIPTION = gql`
  subscription AdvisorWilThreadComment($threadId: String!) {
    scoThreadComment(threadId: $threadId) {
      id
    }
  }
`;
export const AskSection = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    currentUser
  } = useCurrentUser();
  const isMobile = useIsMobile();
  const wilUsage = useWilMessageUsage();
  const [hasMessagesInThread, setHasMessagesInThread] = useState(false);
  const [threadRefetchTrigger, setThreadRefetchTrigger] = useState(0);
  const [wilWhoIsOpen, setWilWhoIsOpen] = useState(false);
  const autoCreateStarted = useRef(false);
  const forceNewStarted = useRef(false);
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(ChatMessageComposer.mutations.upsertChatComment());
  const toolSessionIdFromQuery = typeof router.query.toolSessionId === 'string' ? router.query.toolSessionId : undefined;
  const forceNew = router.query.new === '1' || router.query.new === 'true';
  /** Explicit list view (e.g. toolbar "All chats") — don't auto-open a lone session. */
  const forceList = router.query.list === '1' || router.query.list === 'true';
  const {
    toolQuery,
    toolId,
    toolIconUrl,
    toolUserId,
    wilDisplayName
  } = useWilTool();

  // Always scope to the current user so admins see their own chats, not every user's.
  const {
    sessionsQuery,
    sessions,
    visibleSessions,
    hasMoreSessions,
    loadMoreSessions
  } = useWilSessions({
    toolId,
    userId: currentUser?.userId,
    activeToolSessionId: toolSessionIdFromQuery
  });
  const activeSessionQuery = useQuery(WIL_SESSION_QUERY, {
    variables: {
      toolSessionId: toolSessionIdFromQuery
    },
    skip: !toolSessionIdFromQuery,
    fetchPolicy: 'cache-and-network'
  });
  const [createSession, {
    loading: creatingSession
  }] = useMutation(UPSERT_TOOL_SESSION);

  // Prefer a single stable source so cache-and-network updates from the sessions
  // list cannot flip ChatThread's threadId and remount the whole conversation.
  const activeThreadId = useMemo(() => {
    const fromActive = activeSessionQuery.data?.scoToolSession?.threadId || activeSessionQuery.data?.scoToolSession?.thread?.threadId;
    if (fromActive) {
      return String(fromActive);
    }
    const fromList = sessions.find(s => s.toolSessionId === toolSessionIdFromQuery);
    const listThreadId = fromList?.threadId || fromList?.thread?.threadId;
    return listThreadId ? String(listThreadId) : undefined;
  }, [activeSessionQuery.data?.scoToolSession?.threadId, activeSessionQuery.data?.scoToolSession?.thread?.threadId, sessions, toolSessionIdFromQuery]);

  // Keep a stable push helper — `router` object identity changes often in Next.js
  // and was re-firing open-session effects when listed as a hook dependency.
  const routerRef = useRef(router);
  routerRef.current = router;
  const openSession = useCallback((toolSessionId: string) => {
    // String URL so Request Workspace embeds resolve dynamic segments correctly.
    void routerRef.current.push(paths.withQuery(paths.ask, {
      toolSessionId
    }), undefined, {
      shallow: true
    });
  }, [paths]);
  const goToSessionList = useCallback(() => {
    void routerRef.current.push(paths.withQuery(paths.ask, {
      list: '1'
    }), undefined, {
      shallow: true
    });
  }, [paths]);
  const startNewSession = useCallback(async () => {
    if (!toolId || creatingSession) {
      return;
    }
    // Free-tier users at the message limit should not open empty new chats.
    if (wilUsage.isLimitReached) {
      return;
    }
    try {
      const res = await createSession({
        variables: {
          input: {
            toolId
          }
        }
      });
      const created = res.data?.upsertToolSession?.toolSession;
      const newId = created?.toolSessionId;
      if (newId) {
        await sessionsQuery.refetch();
        openSession(String(newId));
      }
    } catch (err) {
      console.error('Failed to create Wil session', err);
    }
  }, [toolId, creatingSession, createSession, sessionsQuery, openSession, wilUsage.isLimitReached]);

  // No prior sessions → open a new chat automatically (skip when free-tier limit hit)
  useEffect(() => {
    if (!router.isReady || !toolId) {
      return;
    }
    if (toolSessionIdFromQuery || forceNew || forceList) {
      return;
    }
    if (sessionsQuery.loading || wilUsage.loading) {
      return;
    }
    if (wilUsage.isLimitReached) {
      return;
    }
    if (sessions.length > 0) {
      return;
    }
    if (autoCreateStarted.current || creatingSession) {
      return;
    }
    autoCreateStarted.current = true;
    void startNewSession();
  }, [router.isReady, toolId, toolSessionIdFromQuery, forceNew, forceList, sessionsQuery.loading, wilUsage.loading, wilUsage.isLimitReached, sessions.length, creatingSession, startNewSession]);
  const onlySessionId = sessions.length === 1 ? sessions[0]?.toolSessionId : undefined;

  // Exactly one session → open it by default (skip when user asked for the list)
  useEffect(() => {
    if (!router.isReady || !toolId) {
      return;
    }
    if (toolSessionIdFromQuery || forceNew || forceList) {
      return;
    }
    if (sessionsQuery.loading) {
      return;
    }
    if (!onlySessionId) {
      return;
    }
    openSession(String(onlySessionId));
  }, [router.isReady, toolId, toolSessionIdFromQuery, forceNew, forceList, sessionsQuery.loading, onlySessionId, openSession]);

  // ?new=1 → create a session (openSession clears the new flag via query replace)
  useEffect(() => {
    if (!router.isReady || !toolId || !forceNew) {
      return;
    }
    if (forceNewStarted.current || creatingSession) {
      return;
    }
    forceNewStarted.current = true;
    void startNewSession();
  }, [router.isReady, toolId, forceNew, creatingSession, startNewSession]);
  useEffect(() => {
    setHasMessagesInThread(false);
  }, [activeThreadId]);
  const handleHasMessages = useCallback((hasMessages: boolean) => {
    setHasMessagesInThread(prev => prev === hasMessages ? prev : hasMessages);
  }, []);
  const commentsQuery = useQuery(WIL_THREAD_COMMENTS_COUNT, {
    variables: {
      threadId: activeThreadId
    },
    skip: !activeThreadId
  });

  /** Send a starter prompt into the active Wil thread (same pattern as tool chat blank states). */
  const onStartPromptClick = useCallback((body: string) => {
    if (!activeThreadId || wilUsage.isLimitReached || upsertChatCommentMutation.loading) {
      return;
    }
    void upsertChatComment({
      variables: {
        input: {
          threadId: activeThreadId,
          body
        }
      }
    }).then(() => {
      setHasMessagesInThread(true);
      setThreadRefetchTrigger(x => x + 1);
      void commentsQuery.refetch();
      if (wilUsage.isWilMessageLimited) {
        void wilUsage.refetch?.();
      }
    });
  },
  // Intentionally depend on refetch, not the full query result object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [activeThreadId, commentsQuery.refetch, upsertChatComment, upsertChatCommentMutation.loading, wilUsage.isLimitReached, wilUsage.isWilMessageLimited, wilUsage.refetch]);

  // AI streams publish scoThreadComment many times per reply. ChatMessageList already
  // handles live message updates via its own subscription — do NOT refetch sessions /
  // free-tier usage on every chunk (that thrashed the tree and remounted ChatThread).
  const sessionsRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (sessionsRefreshTimer.current) {
        clearTimeout(sessionsRefreshTimer.current);
      }
    };
  }, []);
  useSubscription(WIL_THREAD_COMMENT_SUBSCRIPTION, {
    variables: {
      threadId: activeThreadId
    },
    skip: !activeThreadId,
    onData: () => {
      void commentsQuery.refetch();
      // Debounce list/usage refresh so streaming only updates once after the burst.
      if (sessionsRefreshTimer.current) {
        clearTimeout(sessionsRefreshTimer.current);
      }
      sessionsRefreshTimer.current = setTimeout(() => {
        void sessionsQuery.refetch();
        if (wilUsage.isWilMessageLimited) {
          void wilUsage.refetch?.();
        }
      }, 2000);
    }
  });
  const messageCount = commentsQuery.data?.forum?.thread?.commentsCount ?? 0;
  const threadHasMessages = hasMessagesInThread || messageCount > 0;
  const isCheckingMessages = !!activeThreadId && commentsQuery.loading && !hasMessagesInThread;
  const showIntroHeader = !!activeThreadId && !isCheckingMessages && !threadHasMessages;
  const isChatView = !!toolSessionIdFromQuery;
  const isListView = !isChatView && sessions.length > 0 && !creatingSession && !forceNew && (sessions.length > 1 || forceList);
  const activeSessionTitle = useMemo(() => {
    if (!toolSessionIdFromQuery) {
      return null;
    }
    const fromList = sessions.find(s => s.toolSessionId === toolSessionIdFromQuery);
    if (fromList) {
      return sessionTitle(fromList, toolUserId);
    }
    const loaded = activeSessionQuery.data?.scoToolSession;
    if (loaded) {
      return sessionTitle(loaded as ScoToolSession, toolUserId);
    }
    return wilDisplayName;
  }, [toolSessionIdFromQuery, sessions, activeSessionQuery.data?.scoToolSession, toolUserId, wilDisplayName]);
  const toolbarBreadcrumbs = useMemo(() => {
    const trail = [{
      title: `Ask ${wilDisplayName}`,
      href: paths.ask
    }];
    if (isChatView && activeSessionTitle) {
      trail.push({
        title: activeSessionTitle,
        href: `${paths.ask}?toolSessionId=${toolSessionIdFromQuery}`
      });
    }
    return advisorsBreadcrumbsWithTrail(paths.base, ...trail);
  }, [isChatView, activeSessionTitle, toolSessionIdFromQuery, wilDisplayName]);
  useAdvisorsToolbar({
    breadcrumbs: toolbarBreadcrumbs
  });
  const toolbarMenu = useMemo(() => {
    if (!isChatView) {
      return null;
    }
    return <Menu.Root positioning={{
      placement: 'bottom-end'
    }}>
        <Menu.Trigger asChild>
          <Button size={'sm'} variant={'outline'}>
            Chats
            <LuChevronDown />
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content maxH={'min(24rem, 70vh)'} maxW={'20rem'} minW={'16rem'} overflowY={'auto'}>
              {sessions.map(session => {
              const id = session.toolSessionId;
              if (!id) {
                return null;
              }
              const isActive = id === toolSessionIdFromQuery;
              return <Menu.Item key={id} closeOnSelect value={String(id)} onClick={() => {
                if (!isActive) {
                  openSession(String(id));
                }
              }}>
                    <Text flex={1} fontWeight={isActive ? 'semibold' : 'normal'} lineClamp={1}>
                      {sessionTitle(session, toolUserId)}
                    </Text>
                    {isActive ? <LuCheck /> : null}
                  </Menu.Item>;
            })}
              {sessions.length > 0 ? <Menu.Separator /> : null}
              <Menu.Item disabled={creatingSession} value={'new-session'} onClick={() => {
              void startNewSession();
            }}>
                <LuMessageSquarePlus />
                New Chat
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>;
  }, [isChatView, sessions, toolSessionIdFromQuery, toolUserId, openSession, startNewSession, creatingSession]);
  useSetAdvisorsToolbarRightElements(toolbarMenu);

  // Messages-left / upgrade banner lives under the toolbar (outside ChatThread) so it
  // doesn't shrink or break the chat scroll region.
  const showUsageBanner = isChatView && !!activeThreadId && wilUsage.isWilMessageLimited && wilUsage.remainingMessages <= 10;
  const belowToolbarBanner = useMemo(() => {
    if (!showUsageBanner) {
      return null;
    }
    return <Box bg={'colorPalette.container'} borderBottomWidth={'1px'} borderColor={'border'} flexShrink={0} paddingX={isMobile ? 3 : 4} paddingY={2}>
        <WilMessagePaywall limit={wilUsage.limit} remainingMessages={wilUsage.remainingMessages} usedMessages={wilUsage.usedMessages} variant={'banner'} />
      </Box>;
  }, [showUsageBanner, isMobile, wilUsage.limit, wilUsage.usedMessages, wilUsage.remainingMessages]);
  useSetAdvisorsBelowToolbar(belowToolbarBanner);
  const blankState = useMemo(() => <VStack align={'center'} flex={1} flexGrow={1} justify={'center'} paddingY={6} width={'full'}>
        <Box flexShrink={0} overflowY={'auto'}>
          <VStack align={'center'} gap={isMobile ? 4 : 6} paddingBottom={4} paddingTop={isMobile ? 6 : 10} paddingX={isMobile ? 4 : 6}>
            <VStack gap={1} textAlign={'center'}>
              <Heading size={isMobile ? 'xl' : '3xl'}>What can we get done today?</Heading>
            </VStack>
          </VStack>
        </Box>
        <VStack gap={3} maxWidth={'2xl'} paddingX={isMobile ? 2 : 6} width={'full'}>
          <Wrap gap={2} justify={'center'}>
            {ASK_PROMPTS.map(prompt => <Button key={prompt} borderRadius={'full'} disabled={!activeThreadId || wilUsage.isLimitReached} height={'auto'} loading={upsertChatCommentMutation.loading} paddingX={4} paddingY={3} size={'xs'} variant={'outlineGhost'} onClick={() => onStartPromptClick(prompt)}>
                {prompt}
              </Button>)}
          </Wrap>
          <Text color={'colorPalette.fg/60'} fontSize={'sm'} textAlign={'center'}>
            <Link color={'orange.fg'} fontWeight={'semibold'} href={paths.inbox}>
              Talk to a human advisor
            </Link>
          </Text>
        </VStack>
      </VStack>, [isMobile, activeThreadId, onStartPromptClick, upsertChatCommentMutation.loading, wilUsage.isLimitReached]);
  const sessionNotFound = isChatView && !activeSessionQuery.loading && !activeThreadId && !activeSessionQuery.error && !creatingSession;
  const waitingForSingleOpen = !isChatView && !forceList && !forceNew && sessions.length === 1 && !toolSessionIdFromQuery;
  const waitingForAutoCreate = !isChatView && !isListView && sessions.length === 0 && !wilUsage.isLimitReached && !forceList;

  // Never full-page-block a chat that already has a threadId on usage/sessions
  // background loads — that unmounted ChatThread and looked like a reload loop.
  const loading = !router.isReady || toolQuery.loading || !!toolId && sessionsQuery.loading && sessions.length === 0 && !toolSessionIdFromQuery || isChatView && activeSessionQuery.loading && !activeThreadId || creatingSession || waitingForSingleOpen || waitingForAutoCreate || wilUsage.isWilMessageLimited && wilUsage.loading && !isChatView && !activeThreadId;
  const chatMessageListProps = useMemo(() => ({
    blankState: showIntroHeader ? blankState : undefined,
    containerStackProps: {
      minHeight: 0 as const
    },
    onHasMessages: handleHasMessages,
    refetchTrigger: threadRefetchTrigger
  }), [showIntroHeader, blankState, handleHasMessages, threadRefetchTrigger]);
  const error = toolQuery.error || sessionsQuery.error || activeSessionQuery.error;
  return <Flex direction={'column'} height={'full'} minHeight={0} position={'relative'} width={'full'}>
      <AdvisorsQueryState error={error} loading={loading && !sessionNotFound}>
        {sessionNotFound ? <VStack align={'center'} flex={1} gap={4} justify={'center'} padding={6}>
            <Text color={'colorPalette.fg/70'} textAlign={'center'}>
              That chat could not be found.
            </Text>
            <Button variant={'outline'} onClick={goToSessionList}>
              Back to all chats
            </Button>
          </VStack> : isChatView && activeThreadId ? <Box display={'flex'} flex={1} flexDirection={'column'} minHeight={0}>
            <ChatThread bypassContentPermissions hideHeader chatMessageListProps={chatMessageListProps} hideComposer={wilUsage.isLimitReached} threadId={activeThreadId} />
          </Box> : isListView || wilUsage.isLimitReached ? <Flex direction={'column'} height={'full'} minHeight={0} width={'full'}>
            <Box flex={1} minHeight={0} overflowY={'auto'} paddingTop={8} paddingX={isMobile ? 4 : 6}>
              <VStack align={'center'} gap={6} marginX={'auto'} maxWidth={'lg'} width={'full'}>
                <VStack gap={2} textAlign={'center'}>
                  <Avatar.Root borderRadius={'full'} size={'xl'}>
                    <Avatar.Fallback name={wilDisplayName} />
                    <Avatar.Image src={toolIconUrl || '/assets/people/wil.jpg'} />
                    <AiAvatarBadge />
                  </Avatar.Root>
                  <Heading size={'lg'}>Chats with {wilDisplayName}</Heading>
                  <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                    {sessions.length > 0 ? 'Pick up a previous conversation or start a new one.' : `Your free ${wilDisplayName} messages are used up.`}
                  </Text>
                  {wilUsage.isWilMessageLimited ? <Text color={'orange.fg'} fontSize={'xs'}>
                      {wilUsage.isLimitReached ? 'Free message limit reached' : `${wilUsage.remainingMessages} free messages left`}
                    </Text> : null}
                </VStack>
                {wilUsage.isLimitReached ? <WilMessagePaywall limit={wilUsage.limit} remainingMessages={wilUsage.remainingMessages} usedMessages={wilUsage.usedMessages} /> : null}

                <VStack align={'stretch'} gap={2} paddingBottom={8} width={'full'}>
                  {visibleSessions.map(session => {
                const id = session.toolSessionId;
                if (!id) {
                  return null;
                }
                const title = sessionTitle(session, toolUserId);
                const showSummaryPreview = !!session.generatedSummary?.trim() && isMeaningfulTitle(session.title) && clipTitle(session.generatedSummary.trim().split(/\n/)[0]) !== title;
                return <Card.Root key={id} _hover={{
                  bg: 'colorPalette.subtle'
                }} cursor={'pointer'} transition={'background 120ms ease'} variant={'outline'} onClick={() => openSession(String(id))}>
                        <Card.Body paddingX={4} paddingY={3}>
                          <HStack align={'start'} gap={3}>
                            <Avatar.Root flexShrink={0} size={'sm'}>
                              <Avatar.Fallback name={wilDisplayName} />
                              <Avatar.Image src={toolIconUrl || '/assets/people/wil.jpg'} />
                              <AiAvatarBadge />
                            </Avatar.Root>
                            <VStack align={'stretch'} flex={1} gap={0.5} minW={0}>
                              <Text fontWeight={'semibold'} lineClamp={1}>
                                {title}
                              </Text>
                              <Text color={'colorPalette.fg/55'} fontSize={'xs'}>
                                {sessionSubtitle(session, wilDisplayName)}
                                {session.thread?.commentsCount ? ` · ${session.thread.commentsCount} messages` : ''}
                              </Text>
                              {showSummaryPreview && <Text color={'colorPalette.fg/70'} fontSize={'sm'} lineClamp={2}>
                                  {session.generatedSummary}
                                </Text>}
                            </VStack>
                          </HStack>
                        </Card.Body>
                      </Card.Root>;
              })}
                  {hasMoreSessions ? <Button variant={'outline'} width={'full'} onClick={loadMoreSessions}>
                      Load more
                    </Button> : null}
                  {!wilUsage.isLimitReached ? <Button loading={creatingSession} variant={'cta'} width={'full'} onClick={() => {
                void startNewSession();
              }}>
                      <LuMessageSquarePlus />
                      New Chat
                    </Button> : null}
                  <Text color={'colorPalette.fg/60'} fontSize={'sm'} paddingTop={1} textAlign={'center'}>
                    <Link color={'orange.fg'} cursor={'pointer'} fontWeight={'semibold'} onClick={() => setWilWhoIsOpen(true)}>
                      Who is Wil.ai?
                    </Link>
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </Flex> : null}
      </AdvisorsQueryState>

      <WilWhoIsModal open={wilWhoIsOpen} onOpenChange={setWilWhoIsOpen} />
    </Flex>;
};